import type { OmBannerItem, ProductUnitItem, RelatedKeywordItem } from '@weberus/shared-types';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import noop from 'lodash-es/noop';
import { useAtomValue } from 'jotai';
import { queries } from '@/app/_shared/queries';
import { leisureListReqBodyAtom, leisureHeaderReqBodyAtom } from '@/app/_shared/store/leisure-search';
import { tempGlobalFilterParamsAtom } from '@/app/_shared/store/search-filter';

/** 레저 카테고리 정보 조회 */
export const useLeisureCategoryQuery = () => {
  return useQuery({
    ...queries.list.getLeisureCategory(),
  });
};

/** 레저 검색 헤더 정보 조회 */
export const useLeisureSearchHeaderQuery = () => {
  const searchRequestParams = useAtomValue(leisureHeaderReqBodyAtom);

  return useQuery({
    ...queries.list.getLeisureSearchHeader(searchRequestParams),
  });
};

/** 레저 검색 개수 조회 */
export const useLeisureListSearchCountQuery = () => {
  const searchRequestParams = useAtomValue(leisureHeaderReqBodyAtom);
  const tempGlobalFilterParams = useAtomValue(tempGlobalFilterParamsAtom);

  return useQuery({
    ...queries.list.getLeisureSearchCount({
      ...searchRequestParams,
      filters: tempGlobalFilterParams,
    }),
    placeholderData: keepPreviousData, // 이전 데이터 유지
  });
};

/** 레저 검색 리스트 조회 */
export const useLeisureListSearchListQuery = () => {
  const { ref, inView } = useInView({ rootMargin: '500px 0px' });
  const listRequestBody = useAtomValue(leisureListReqBodyAtom);
  const { queryKey, queryFn } = queries.list.getLeisureSearch(listRequestBody!);

  const queryResult = useInfiniteQuery({
    queryKey,
    queryFn,
    enabled: !!listRequestBody,
    placeholderData: keepPreviousData, // 이전 데이터 유지
    initialPageParam: 1,
    getNextPageParam: (params) => (!params?.paging?.isLast ? (params?.paging?.current ?? 1) + 1 : undefined),
    gcTime: 1000 * 60 * 30, //30분 후 GC
    staleTime: 1000 * 60 * 30, //30분 후 캐시 만료
    meta: { persist: true },
  });
  const { data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage, isRefetching } = queryResult;
  const isDataLoading = isLoading || isFetchingNextPage || isRefetching;

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  /**
   * 그룹으로 묶는 이유
   * @see {@link https://screen.yanolja.in/PWjtc1MxteprDs24}
   * 사진처럼 중간에 광고 배너가 들어가는 경우가 있을 경우 리스트 그룹핑을 다시 하므로 다음과 같은 형태로 변경
   * */
  /** Update: 2025-01-14
   * 첫번째 페이지 조회시 항상 OM 배너, 연관 검색어, 광고 목록 전체가 조회되므로 첫번째 페이지만 필터링하고 이후 페이지는 PRODUCT_ITEM 으로 그룹화
   */
  const firstPage =
    data?.pages[0]?.items.reduce<(ProductUnitItem[] | OmBannerItem | RelatedKeywordItem)[]>((group, item) => {
      if (item.type === 'PRODUCT_ITEM' || item.type === 'BANNER_TYPE_PRODUCT_ITEM') {
        const lastGroup = group[group.length - 1];
        Array.isArray(lastGroup) ? lastGroup.push(item) : group.push([item]);
      } else if (item.type === 'RELATED_KEYWORD' || item.type === 'ONSITE_MARKETING_BANNER') {
        group.push(item);
      }
      return group;
    }, []) || [];

  // 첫번째 페이지 필터링 후 마지막 항목이 PRODUCT_ITEM 그룹인 경우 다른 페이지와의 그룹화을 위해 분리
  let lastProductItems = null;
  if (Array.isArray(firstPage[firstPage.length - 1])) {
    lastProductItems = firstPage.pop() as ProductUnitItem[];
  }

  const otherPages = [
    ...(lastProductItems ? lastProductItems : []),
    ...(data?.pages.slice(1).flatMap((page) => page.items) || []),
  ] as ProductUnitItem[];

  const filteredList = [...firstPage, ...(otherPages.length ? [otherPages] : [])];

  return {
    ...queryResult,
    isDataLoading,
    filteredList,
    lastItemRef: hasNextPage ? ref : noop,
    serverLogMeta: data?.pages[0]?.serverLogMeta || {},
    isFirstPageLoaded: !!data?.pages[0],
  };
};
